/**
 * @param {string} email An email
 * @return {Promise} A promise
 */
import Cookies from 'cookies-js';

import { SSO_HOST } from 'common/configuration/constants';
import request from 'common/tools/network/request';

type Profile = {
  nickname: string;
  email?: string;
  gender?: 'male' | 'female' | 'undisclosed';
  birthDate?: string;
  cguValidated?: boolean;
  adConsent?: boolean;
  pixelConsent?: boolean;
};

export type Optins = {
  newsletterOptin?: boolean;
  notificationOptin?: boolean;
  serieOptin?: boolean;
  streamingOptin?: boolean;
  sceanceOptin?: boolean;
  kidsOptin?: boolean;
  lesindesOptin?: boolean;
  classiquesOptin?: boolean;
};

const tuple = <T extends readonly string[]>(...args: T) => args;

const NEWSLETTER_OPTINS_KEYS = tuple(
  'newsletterOptin',
  'notificationOptin',
  'serieOptin',
  'streamingOptin',
  'kidsOptin',
  'lesindesOptin',
  'classiquesOptin'
);

type NewsletterOptinKey = (typeof NEWSLETTER_OPTINS_KEYS)[number];

type NewsletterOptinsData = Record<NewsletterOptinKey, boolean | null>;

type NewsletterOptinsResponse = {
  data: NewsletterOptinsData | null;
};

export const buildAllNewsletterOptins = (value: boolean): Optins => ({
  newsletterOptin: value,
  notificationOptin: value,
  serieOptin: value,
  streamingOptin: value,
  kidsOptin: value,
  lesindesOptin: value,
  classiquesOptin: value
});

export type SocialConnectPayload = {
  token: string;
  profile?: {
    nickname?: string;
    cguValidation?: boolean;
  };
};

type DoRequestArgs<PayloadType extends { [key: string]: any } = any> = {
  endpoint: string;
  method?: string;
  payload?: PayloadType;
  withCredentials?: boolean;
};

export enum connectionErrors {
  ACCOUNT_EXISTS = 'ACCOUNT_EXISTS',
  SOCIAL_NO_MAIL = 'SOCIAL_NO_MAIL'
}

const doRequest = <
  ResponseType = any,
  PayloadType extends { [key: string]: any } = any
>({
  endpoint,
  method = 'POST',
  payload,
  withCredentials = false
}: DoRequestArgs<PayloadType>) => {
  const headers: { [key: string]: string } = {
    'Content-Type': 'application/json'
  };
  const token = Cookies.get('GraphToken');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const body: RequestInit & { payload?: PayloadType } = {
    method,
    payload,
    headers
  };

  if (withCredentials) {
    body['credentials'] = 'include';
  }
  return request<ResponseType, PayloadType>(`${SSO_HOST}${endpoint}`, body);
};
export const isEmailValid = (email: string) =>
  doRequest({
    endpoint: '/api/account/validity',
    payload: { email }
  });

export const connectWithGoogle = (payload: SocialConnectPayload) =>
  doRequest({
    endpoint: '/api/account/connect/google',
    payload,
    withCredentials: true
  });

export const connectWithFacebook = (payload: SocialConnectPayload) =>
  doRequest({
    endpoint: '/api/account/connect/facebook',
    payload,
    withCredentials: true
  });

export const connectWithLoginPassword = (login: string, password: string) =>
  doRequest({
    endpoint: '/api/account/connect',
    payload: {
      email: login,
      password: password
    },
    withCredentials: true
  });

export const getNewsletterOptins = () =>
  doRequest<NewsletterOptinsResponse | null>({
    endpoint: '/api/account/newsletter-optins',
    method: 'GET'
  });

/**
 * Refusing marketing newsletters in onboarding must not turn off product
 * notifications (push/in-app style opt-in): notificationOptin is always forced to true.
 */
const withNotificationOptinTrue = (optins: Optins): Optins => ({
  ...optins,
  notificationOptin: true
});

export const buildNewsletterOptinsFromApi = async (
  value: boolean
): Promise<Optins> => {
  try {
    if (value) {
      return buildAllNewsletterOptins(true);
    }
    const current = await getNewsletterOptins();
    const data = current?.data;
    if (!data) {
      return withNotificationOptinTrue(buildAllNewsletterOptins(false));
    }

    const optins: Optins = {};
    for (const key of NEWSLETTER_OPTINS_KEYS) {
      if (typeof data[key] === 'boolean') {
        optins[key] = value;
      }
    }

    return Object.keys(optins).length > 0
      ? withNotificationOptinTrue(optins)
      : withNotificationOptinTrue(buildAllNewsletterOptins(false));
  } catch {
    return withNotificationOptinTrue(buildAllNewsletterOptins(false));
  }
};

export const disableAccount = () =>
  doRequest({
    endpoint: '/api/account',
    method: 'DELETE'
  });

export const updateNewsletterOptins = (optins: Optins) =>
  doRequest({
    endpoint: '/api/account/newsletter-optins',
    payload: optins
  });

export const changePassword = (oldPassword: string, newPassword: string) =>
  doRequest({
    endpoint: '/api/account/change-password',
    payload: {
      password: oldPassword,
      'new-password': newPassword
    }
  });

export const updateProfile = ({
  nickname,
  email,
  gender,
  birthDate,
  cguValidated,
  adConsent,
  pixelConsent
}: Partial<Profile>) =>
  doRequest({
    endpoint: '/api/account/update-profile',
    payload: {
      ...(nickname ? { nickname } : {}),
      ...(email ? { email } : {}),
      ...(gender ? { gender } : {}),
      ...(birthDate ? { birthDate } : {}),
      ...(cguValidated !== undefined ? { cguValidated } : {}),
      ...(adConsent !== undefined ? { adConsent } : {}),
      ...(pixelConsent !== undefined ? { pixelConsent } : {})
    }
  });

export const followFacebookFriends = (facebookToken: string) =>
  doRequest({
    endpoint: '/api/account/follow-fb-friends',
    payload: {
      token: facebookToken
    }
  });

export const resendActivationEmail = () =>
  doRequest({
    endpoint: '/api/account/resend-activation-email'
  });
